import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, SimpleChanges,forwardRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ICONS } from '../../../_helpers/icons.constants';
import { DEFAULT_TEXTBOX_CONFIG, TextboxConfig } from './textbox.modal';
import { ReactiveFormsModule } from '@angular/forms';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type InputType = 'text' | 'password' | 'email' | 'number' | 'date' | 'tel' | 'search' | 'input';

@Component({
  selector: 'ds-textbox',
  templateUrl: './textbox.component.html',
  styleUrls: ['./textbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextboxComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule]
})
export class TextboxComponent implements OnInit, ControlValueAccessor {
  public ICONS = ICONS;
  @Output() valueChange = new EventEmitter<string | number | null>();
  @Output() blur = new EventEmitter<void>();
  @Output() keypress = new EventEmitter<KeyboardEvent>();

  @Input() textboxInput: TextboxConfig = { ...DEFAULT_TEXTBOX_CONFIG };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.textboxInput) {
      const val: TextboxConfig = changes.textboxInput.currentValue || {};
      this.textboxInput = { ...DEFAULT_TEXTBOX_CONFIG, ...val };
      this.currentLength = String(this.textboxInput.value ?? '').length;
    }
  }

  /**
   * Where the src icon sits:
   * 1. Currency → always right
   * 2. Explicit iconPosition set → respect it
   * 3. Src + validation, no explicit position → src goes left so validation can go right
   * 4. Default → provided iconPosition (default 'right')
   */
  get effectiveIconPosition(): 'left' | 'right' {
    if (this.textboxInput?.currencyText) return 'right';
    if (this.textboxInput?.iconSrc && this.textboxInput?.iconPosition) return this.textboxInput.iconPosition;
    if (this.textboxInput?.iconSrc && (this.textboxInput?.isValid || this.textboxInput?.isInvalid)) return 'left';
    return this.textboxInput?.iconPosition || 'right';
  }

  /** Whether the src icon renders in the left slot */
  get hasLeftIcon(): boolean {
    return !!(this.textboxInput?.iconSrc && this.effectiveIconPosition === 'left');
  }

  /** Whether anything renders in the right slot (src on right, validation, or password toggle) */
  get hasRightIcons(): boolean {
    const hasSrcRight = !!(this.textboxInput?.iconSrc && this.effectiveIconPosition === 'right');
    const hasValidation = !!(this.textboxInput?.isValid || this.textboxInput?.isInvalid);
    const hasPassword = this.textboxInput?.type === 'password';
    return hasSrcRight || hasValidation || hasPassword;
  }

  /** Validation icons always render in the right slot */
  get showValidationOnRight(): boolean {
    return !!(this.textboxInput?.isValid || this.textboxInput?.isInvalid);
  }

  /** Semantic classes that drive input padding via SCSS */
  get iconConfigClasses(): { [key: string]: boolean } {
    const hasValidation = !!(this.textboxInput?.isValid || this.textboxInput?.isInvalid);
    const hasSrcIcon = !!this.textboxInput?.iconSrc;
    const hasPassword = this.textboxInput?.type === 'password';
    const srcIsLeft = this.hasLeftIcon;
    const srcIsRight = hasSrcIcon && !srcIsLeft;

    return {
      '--has-src-left': srcIsLeft,
      '--has-src-right': srcIsRight,
      '--has-validation-right': hasValidation,
      '--has-password': hasPassword,
      '--src-and-validation-right': srcIsRight && hasValidation,
      '--src-and-password-right': srcIsRight && hasPassword,
      '--validation-and-password-right': hasValidation && hasPassword,
      '--src-validation-and-password-right': srcIsRight && hasValidation && hasPassword,
    };
  }

  /** All classes for the inner wrapper div — merged in TS to avoid spread operator in templates */
  get innerDivClasses(): { [key: string]: boolean } {
    return {
      '--currency-text-input': !!this.textboxInput?.currencyText,
      '--is-icon': !!(this.textboxInput?.iconSrc || this.textboxInput?.type === 'password' || this.textboxInput?.isValid || this.textboxInput?.isInvalid),
      '--input-type-validation': !!(this.textboxInput?.isValid || this.textboxInput?.isInvalid),
      ...this.iconConfigClasses,
    };
  }

  onInputChange(value: string) {
    this.textboxInput.value = value;
    this.valueChange.emit(value);
    this.onChange(value);
  }
  // internal
  showPassword = false;
  currentLength = 0;
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    if (!this.textboxInput.id) {
      this.textboxInput.id = `ds-textbox-${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  focus(): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  onInput(ev: Event) {
    const target = ev.target as HTMLInputElement;
    this.textboxInput.value = target.value;
    this.currentLength = target.value.length;
    this.valueChange.emit(this.textboxInput.value);
    this.onChange(this.textboxInput.value);
  }

  onBlur(ev: FocusEvent) {
    this.blur.emit();
    this.onTouched();
  }

  onKeypress(ev: KeyboardEvent) {
    this.keypress.emit(ev);
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.textboxInput = this.textboxInput || { ...DEFAULT_TEXTBOX_CONFIG };
    this.textboxInput.value = value ?? '';
    this.currentLength = String(this.textboxInput.value ?? '').length;
    if (this.inputElement && this.inputElement.nativeElement) {
      this.inputElement.nativeElement.value = this.textboxInput.value as any;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.textboxInput) {
      this.textboxInput = { ...DEFAULT_TEXTBOX_CONFIG };
    }
    this.textboxInput.disabled = isDisabled;
  }
}